import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ModeratAnfrage} from "../anfrage/moderat.anfrage";
import {ModeratRequestService} from "../services/request.service";
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {MatCheckboxChange} from "@angular/material/checkbox";
import {ElementService} from "../../services/element.service";
import {ActivatedRoute, Router} from "@angular/router";
import {PortalService} from "../../services/portal.service";
import {EmbedService} from "../../services/embed.service";
import {StepService} from "../../services/step.service";

/**
 * Component for the entry of the moderat. Let the user select its interests.
 */
@Component({
  selector: 'app-moderat-einstieg',
  templateUrl: './einstieg.component.html'
})
export class EinstiegComponent implements OnInit {

  /**
   * List of interests that we want to ask the user for
   */
  interestList = ['alles', 'massnahme'];

  /**
   * If interest=massanhme, this list of items is shown to the user to be selected
   */
  measureList = ['heating', 'dach', 'fenster', 'fassade', 'kellerdecke', 'solarthermie'];

  /**
   * the current userdata
   */
  anfrage: ModeratAnfrage;

  /**
   * placeholder for portalname in welcome text
   */
  welcomePlaceholder: any = {};

  /**
   * form for validation
   */
  form: UntypedFormGroup;

  /**
   * Toggle to show the short version of the entry
   */
  shortVersion: boolean = false;

  /**
   * target of the link of the short version
   */
  shortVersionButtonUrl: string;

  /**
   * Toggle for showing the error if no option is selected
   */
  showError: boolean = false;

  /**
   * formfield for saving the interests
   * @private
   */
  private static readonly FIELD_INTERESSE: string = 'interesse';

  /**
   * formfield for the measures
   * @private
   */
  private static readonly FIELD_MASSNAHMEN: string = 'interesse_massnahmen';

  /**
   * html element that we want to scroll to after the iuser has selected an interest.
   */
  @ViewChild('submitWrapper') submitWrapper: ElementRef;

  /**
   * constructor
   * @param requestService
   * @param router
   * @param route
   * @param elementService
   * @param activatedroute
   * @param portalService
   * @param embedService
   * @param stepService
   */
  constructor(private requestService: ModeratRequestService, private router: Router, private route: ActivatedRoute, private elementService: ElementService,
              private activatedroute: ActivatedRoute, protected portalService: PortalService, private embedService: EmbedService, private stepService: StepService) {}

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    // check if we should show the short version
    this.activatedroute.data.subscribe(data => {
      this.shortVersion = data?.version === 'short';

      // try to get button URL from the portal config, fallback to local URL if it is not there
      if(this.shortVersion === true) {
        this.shortVersionButtonUrl = this.portalService.getConfig('moderat.short.buttonlink');
        if(this.shortVersionButtonUrl === null) {
          this.shortVersionButtonUrl = this.router.url + '/../verbrauchsanalyse/start';
        }
      }
      else if(this.embedService.isEmbedded() === true && this.embedService.isInitialCall() === true) {
        const lastUrl = this.stepService.getLastUrl();
        if(lastUrl) {
          this.router.navigate([lastUrl]);
        }
      }
    });

    // get the anfrage from the local cache
    this.anfrage = this.requestService.getAnfrage();
    this.anfrage.mode = ModeratAnfrage.MODE_STANDARD;

    this.activatedroute.queryParams.subscribe(params => {
      // show first chapter start page if requested
      if(params?.step === 'chapter1') {
        this.nextStep();
      }
      // show second chapter startpage if requested
      if(params?.step === 'chapter2' && this.anfrage?.verbrauchsanalyseResult?.status?.code === 0) {
        this.router.navigate(['../bestandsaufnahme/start'], {relativeTo: this.route});
      }
    })

    // initialize the form
    this.initForm();

    //init Placeholder
    if(this.portalService.getConfig('embed.portal.welcometext')) {
     this.welcomePlaceholder = {
       portalPlaceholder: ' ' + this.portalService.getConfig('embed.portal.welcometext')
     }
    }
    else {
      this.welcomePlaceholder = {
        portalPlaceholder: ''
      }
    }
  }

  /**
   * Inits the form with all the needed input fields.
   * @private
   */
  private initForm(): void {
    // create form and add inputs
    this.form = new UntypedFormGroup({});
    this.form.addControl(EinstiegComponent.FIELD_INTERESSE, new UntypedFormControl(this.anfrage.interesse, [Validators.required]));
    this.form.addControl(EinstiegComponent.FIELD_MASSNAHMEN, new UntypedFormControl(this.anfrage.interesseMassnahmen, [Validators.required]));

    // disable the measure fields because it's not available on the first view
    this.form.get(EinstiegComponent.FIELD_MASSNAHMEN).disable({onlySelf: true});

    // subscribe to changes on the form
    this.form.get(EinstiegComponent.FIELD_INTERESSE).valueChanges.subscribe((value) => this.updateValidators(value));
  }

  /**
   * Update the validators every time the form changes, disable/enable meausre validator
   * @param value
   */
  updateValidators(value: string): void {
    if(value === 'massnahme') {
      // activate interesse_massnahme
      this.form.get(EinstiegComponent.FIELD_MASSNAHMEN).enable({onlySelf: true});
    }
    else {
      // disable interesse_massnahme
      this.form.get(EinstiegComponent.FIELD_MASSNAHMEN).disable({onlySelf: true});
    }
  }

  /**
   * The form is submitted
   *
   */
  onSubmit(): void {
    if(this.form.valid === true) {
      this.saveData();

      // show text Step
      this.nextStep();
    }
  }

  /**
   * Click on the submit button
   */
  onSubmitClick(): void {
    if(this.form.valid === false) {
      this.showError = true;
    }
  }

  /**
   * save the selection of interests
   */
  saveData(): void {
    // in some cases we can have no intersts, because the user did not choose one. Fallback here.
    if(this.form.get(EinstiegComponent.FIELD_INTERESSE).value === null) {
      this.form.get(EinstiegComponent.FIELD_INTERESSE).setValue(this.interestList[0]);
    }

    // save data to local browser
    this.anfrage.interesse = this.form.get(EinstiegComponent.FIELD_INTERESSE).value;
    this.anfrage.interesseMassnahmen = this.anfrage.interesse === 'massnahme'? this.form.get(EinstiegComponent.FIELD_MASSNAHMEN).value : null;
    this.requestService.setAnfrage(this.anfrage);

    // save to server
    this.requestService.saveDataToServer()
  }

  /**
   * moveon to the next step
   */
  nextStep(): void {
    // forward to first chapter
    this.router.navigate(['../verbrauchsanalyse/start'], {
      relativeTo: this.route,
      state: {external: this.anfrage?.extraFields && Object.keys(this.anfrage.extraFields).length > 0}}
    );
  }

  /**
   * If an interest is selected, scroll to the list of measures.
   */
  interestSelected() {
    this.showError = false;
    setTimeout(() => {
      this.elementService.scrollToElement(this.submitWrapper.nativeElement);
    }, 100)
  }

  /**
   * Eventhandler if a measure is changed
   * @param checkbox
   */
  selectMeasure(checkbox: MatCheckboxChange): void {
    // get current value
    let selectedMeasures = this.form.get(EinstiegComponent.FIELD_MASSNAHMEN).value? this.form.get(EinstiegComponent.FIELD_MASSNAHMEN).value.split(",") : [];

    if(checkbox.checked === true) {
      selectedMeasures.push(checkbox.source.value);
    }
    else {
      selectedMeasures = selectedMeasures.filter(element => element !== checkbox.source.value);
    }
    this.form.get(EinstiegComponent.FIELD_MASSNAHMEN).setValue(selectedMeasures.join(","));
  }

  /**
   * Checks if a measure is selected
   * @param measure
   */
  hasMeasure(measure: string): boolean {
    let selectedMeasures = this.form.get(EinstiegComponent.FIELD_MASSNAHMEN).value? this.form.get(EinstiegComponent.FIELD_MASSNAHMEN).value.split(",") : [];
    return selectedMeasures.indexOf(measure) > -1;
  }

}
